// TanStack Query key factory.
//
// Centralizes every query key family used across the web app so that query
// hooks, mutations, and the WebSocket invalidation logic share identical key
// shapes. Each factory method returns a readonly tuple typed with `as const`
// so callers preserve exact element types (per §FE.4.1/§FE.4.2).
//
// Overload note for `connections.list`: calling with a single argument returns
// a 2-element tuple so that invalidateQueries prefix-matches the 3-element
// list keys (exact: false default). Passing `undefined` explicitly as the
// second argument returns a 3-element tuple, matching the cache-seed sites in
// useTopicsQuery.ts / useTopicQuery.ts that key on ['connections', id, undefined].

function connectionsList(topicId: string | undefined): readonly ['connections', string | undefined];
function connectionsList(
  topicId: string | undefined,
  type: string | undefined
): readonly ['connections', string | undefined, string | undefined];
function connectionsList(
  topicId: string | undefined,
  type?: string
):
  | readonly ['connections', string | undefined]
  | readonly ['connections', string | undefined, string | undefined] {
  if (arguments.length >= 2) {
    return ['connections', topicId, type] as const;
  }
  return ['connections', topicId] as const;
}

export const queryKeys = {
  topics: {
    all: ['topics'] as const,
    list: (page: number, pageSize: number) => ['topics', page, pageSize] as const,
  },
  topic: (topicId: string | undefined) => ['topic', topicId] as const,
  topicMessages: (topicId: string | undefined) => ['topic-messages', topicId] as const,
  connections: {
    all: ['connections'] as const,
    list: connectionsList,
  },
  connection: (topicId: string | undefined, connectionId: string | undefined) =>
    ['connection', topicId, connectionId] as const,
  connectionTypes: {
    byEnvironment: (isDevelopment: boolean) => ['connectionTypes', isDevelopment] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    count: ['notifications', 'count'] as const,
    list: (unreadOnly: boolean | undefined, limit: number | undefined) =>
      ['notifications', unreadOnly, limit] as const,
  },
  slackChannels: (topicId: string | undefined, connectionId: string | undefined) =>
    ['slack-channels', topicId, connectionId] as const,
  discordChannels: (guildId: string | undefined) => ['discord-channels', guildId] as const,
  oauthClientMetadata: (clientId: string | null) => ['oauthClientMetadata', clientId] as const,
  usageSummary: ['usage-summary'] as const,
  telegramToken: (token: string | null) => ['telegram-token', token] as const,
  user: ['user'] as const,
  authorizedApps: ['authorizedApps'] as const,
} as const;
